import type { DateSelectArg } from '@fullcalendar/core';
import type { Absence, ResourceUser } from '@src/types';
import { formatIsoDate, isNextDay, subtractDay, toTime } from '@src/utils';
import { ABSENCE_BLOCK_POSITION_SINGLE } from '../constants';
import type { AbsenceSelectionData, TimelineSlot } from '../types';
import {
  getAbsencesIdsForRange,
  getEventLabel,
  getUserAbsences,
} from './absence';
import { getUserWorktimeForDate } from './resources';

/**
 * Collects absence details for a given absence ID.
 *
 * Retrieves key attributes from the targeted absence record and, if available,
 * from its associated neighbors (whole activity).
 *
 * @param {number} absenceId
 *        Unique identifier of the absence to collect.
 * @param {Array<Absence>} absences
 *        List of absence objects to search through.
 * @param {boolean} single
 *        Gets data for single absenece object with specified id or
 *        for all absences if it is multiday absence event
 */
export function getAbsenceSelectionById(
  absenceId: number,
  absences: Absence[],
  single = false,
): AbsenceSelectionData | null {
  if (!absenceId || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const anchor = absences.find((a) => a.id === absenceId);
  if (!anchor) {
    return null;
  }

  if (
    single ||
    !anchor.allDay ||
    anchor.blockPosition === ABSENCE_BLOCK_POSITION_SINGLE
  ) {
    return {
      userId: anchor.userId,
      start: formatIsoDate(anchor.date),
      end: formatIsoDate(anchor.date),
      startTime: anchor.startTime,
      endTime: anchor.endTime,
      absenceIds: [anchor.id],
    };
  }

  const sameGroup = (a: Absence) => {
    return a.userId === anchor.userId && a.allDay && a.typeId === anchor.typeId;
  };

  // Collect all candidate items for this user/type
  const candidates = absences
    .filter(sameGroup)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const anchorIdx = candidates.findIndex((a) => a.id === absenceId);
  if (anchorIdx === -1) {
    return null;
  }

  // Expand left while consecutive by date (-1 day steps)
  let left = anchorIdx;
  while (
    left - 1 >= 0 &&
    isNextDay(candidates[left - 1].date, candidates[left].date)
  ) {
    left -= 1;
  }

  // Expand right while consecutive by date (+1 day steps)
  let right = anchorIdx;
  while (
    right + 1 < candidates.length &&
    isNextDay(candidates[right].date, candidates[right + 1].date)
  ) {
    right += 1;
  }

  const block = candidates.slice(left, right + 1);
  return {
    userId: block[0].userId,
    start: formatIsoDate(block[0].date),
    end: formatIsoDate(block[block.length - 1].date),
    startTime: null,
    endTime: null,
    absenceIds: block.map((a) => a.id),
  };
}

export function getSlotDatefromSelection(
  info: DateSelectArg,
  absences: Absence[],
): TimelineSlot {
  const end = info.allDay ? subtractDay(info.end, 1) : info.end;
  const userId = info.resource?.extendedProps.userId;
  const userAbsences = getUserAbsences(userId, absences);
  const slot: TimelineSlot = {
    resourceId: info?.resource?.id,
    start: formatIsoDate(info.start),
    end: formatIsoDate(end),
    startTime: info.allDay ? null : toTime(info.start),
    endTime: info.allDay ? null : toTime(info.end),
    selectedIds: getAbsencesIdsForRange(info.start, end, userAbsences),
    userId: info.resource?.extendedProps.userId,
  };
  return slot;
}

/*
   Returns an array of free dates  within the slot range that are
   not occupied by absences.
*/
export function getSlotFreeDates(
  slot: TimelineSlot,
  absences: Absence[],
): Date[] {
  if (!slot || !slot.start || !slot.end) {
    return [];
  }

  if (slot.end < slot.start) {
    return [];
  }

  const occupied = new Set(
    (absences || [])
      .filter((a) => !(a.dateStr < slot.start) && !(a.dateStr > slot.end))
      .map((a) => a.dateStr),
  );
  const free = [];
  let cur = new Date(slot.start);

  while (cur <= new Date(slot.end)) {
    if (!occupied.has(formatIsoDate(cur))) {
      free.push(cur);
    }
    const nextDate = cur.getDate() + 1;
    cur = new Date(cur);
    cur.setDate(nextDate);
  }
  return free;
}

export function getTransientEventsForSlot(
  slot: TimelineSlot | null,
  absences: Array<Absence>,
  users: Array<ResourceUser>,
  dayView = false,
) {
  if (!slot) {
    return [];
  }
  const userAbsences = getUserAbsences(slot.userId, absences);
  const slotFreeDates = getSlotFreeDates(slot, userAbsences);
  if (!slotFreeDates.length) {
    return [];
  }

  if (dayView) {
    const userWorktime = getUserWorktimeForDate(slot.userId, users, slot.start);
    const startTime = slot.startTime || userWorktime?.start;
    const endTime = slot.endTime || userWorktime?.end;
    const label = getEventLabel(slot.start, slot.end, true);
    return [
      {
        id: `transient-event-${slot.start}`,
        resourceId: slot.resourceId,
        start: formatIsoDate(slot.start, startTime),
        end: formatIsoDate(slot.end, endTime),
        classNames: ['transient-event'],
        extendedProps: { label: label },
        startEditable: false,
        durationEditable: true,
        resourceEditable: false,
      },
    ];
  }
  // const user = users.find((u) => u.id === slot.userId);
  // const worktime = user.worktime;

  const transientEvents = slotFreeDates.map((day) => {
    const label = getEventLabel(day, day, dayView);
    const event = {
      id: `transient-event-${formatIsoDate(day)}`,
      resourceId: slot.resourceId,
      start: formatIsoDate(day),
      end: formatIsoDate(day),
      classNames: ['transient-event'],
      extendedProps: { label: label },
      editable: false,
    };

    // const dayNumber = day.day();
    // const wt = worktime.find((item) => item.dewt_weekday + 1 === dayNumber); // FIXME
    // if (wt) {
    //   const [sh, sm] = wt.dewt_start_time_morning.split(':');
    //   const [eh, em] = wt.dewt_end_time_afternoon.split(':');
    //   event.start = toUtcDateTime(day.hour(sh).minute(sm));
    //   event.end = toUtcDateTime(day.hour(eh).minute(em));
    // }

    return event;
  });
  return transientEvents;
}
